/**
 * 설치 모달의 판정 (DOM 없음 — tests/unit/pwa-install-logic.test.mjs).
 *
 * 경우(installCase):
 *   installed  — 설치한 앱 안에서 실행 중 (설치 항목을 모두 숨긴다)
 *   samsung    — 삼성 인터넷: 그 브라우저의 WebAPK 가 안드로이드 14+ 경고를 띄워 크롬으로 보낸다(spec 2026-09-24 §0.1).
 *                설치 창을 쏘더라도 이쪽이 먼저다.
 *   prompt     — 브라우저 설치 창(beforeinstallprompt)이 준비됨: 모달 없이 곧바로 그 창
 *   elsewhere  — 이 브라우저에 이미 설치됨(getInstalledRelatedApps) — 탭에서 보고 있을 뿐
 *   ios        — 아이폰 · 아이패드: 공유 → 홈 화면에 추가
 *   chromium   — 데스크톱 크롬 · 에지 · 웨일: 설치 창이 아직 없음(크롬은 한 번 클릭 + 30초 머문 뒤에야 준다,
 *                web.dev install-criteria) — 주소창의 설치 아이콘은 지금도 된다
 *   android    — 안드로이드 크롬 계열: ⋮ → 앱 설치
 *   mac        — macOS Safari 17+: 파일 → Dock에 추가
 *   other      — 그 밖(파이어폭스 등)
 */

export function isIOS(ua, maxTouchPoints) {
    return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && Number(maxTouchPoints) > 1);
}

// 크롬 계열 브라우저의 이름: 'edge' · 'whale' · 'chrome', 아니면 ''.
export function chromiumBrowser(ua, brands) {
    const names = (brands || []).map((b) => String((b && b.brand) || ''));
    if (/Edg(e|A|iOS)?\//.test(ua) || names.some((n) => /Edge/.test(n))) return 'edge';
    if (/Whale\//.test(ua) || names.some((n) => /Whale/.test(n))) return 'whale';
    if (/Firefox\/|FxiOS\//.test(ua)) return '';
    if (names.some((n) => /Chromium|Google Chrome/.test(n)) || /Chrome\/|Chromium\//.test(ua)) return 'chrome';
    return '';
}

function isMacSafari(ua) {
    return /Macintosh/.test(ua) && /Version\/\d+[\d.]* .*Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\/|Firefox\//.test(ua);
}

export function installCase({ ua = '', brands = null, mobile = false, maxTouchPoints = 0, standalone = false, prompt = false, installed = false } = {}) {
    if (standalone) return 'installed';
    if (/SamsungBrowser/i.test(ua)) return 'samsung';
    if (prompt) return 'prompt';
    if (installed) return 'elsewhere';
    if (isIOS(ua, maxTouchPoints)) return 'ios';
    if (chromiumBrowser(ua, brands)) return mobile || /Android/.test(ua) ? 'android' : 'chromium';
    if (isMacSafari(ua)) return 'mac';
    return 'other';
}

// 「앱 설치」 항목을 보일지. 아바타 메뉴(넓은 화면)는 설치할 길을 안내할 수 있을 때만 — 파이어폭스처럼 "브라우저 메뉴를
// 보세요"만 남는 경우와 이미 설치된 브라우저는 소음이다. 폰 「전체」 시트도 이미 설치된 브라우저에서는 숨긴다.
// 로그인 화면의 링크는 설치한 앱에서만 숨는다 — 이미 설치된 브라우저에는 「앱으로 여세요」를 알려 준다.
export function installEntryHidden(ctx, place) {
    if (ctx === 'installed') return true;
    if (place === 'menu') return ctx === 'other' || ctx === 'elsewhere';
    if (place === 'all') return ctx === 'elsewhere';
    return false;
}

// getInstalledRelatedApps 의 답에 우리 앱(platform webapp)이 있는가.
export function relatedWebappInstalled(apps) {
    return Array.isArray(apps) && apps.some((a) => a && a.platform === 'webapp');
}
